// @ts-check
// Painel interno de suporte (app.<domínio>/admin, contrato 08 de cobrança e operação, seção 7.4): entrada por código
// para e-mails de SUPERADMIN_EMAILS e, depois, a moldura no layout do CRM com as seções Clientes (visão geral e
// instituições), Financeiro (cobranças, notas fiscais, pedidos do site e cupons) e Plataforma (códigos de acesso,
// envio de e-mails, backups e auditoria). As telas ficam em ./admin/.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { fetchAdminSession, requestAdminEmailCode, signOutAdmin, verifyAdminEmailCode } from '../../data/operations.js';
import { AuthLayout, AuthTextField } from '../auth/auth-layout.js';
import { EMAIL_CODE_LENGTH, EmailCodeField } from '../auth/email-code-field.js';
import { MainButton } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { resolveAdminRoute } from '../../lib/admin-navigation.js';
import { operationsErrorMessage } from './operations-format.js';
import { AdminShell } from './admin/admin-shell.js';
import { AdminOverviewPage } from './admin/admin-overview-page.js';
import { AdminInstitutionsPage } from './admin/admin-institutions-page.js';
import { AdminInstitutionPage } from './admin/admin-institution-page.js';
import { AdminPaymentsPage } from './admin/admin-payments-page.js';
import { AdminInvoicesPage } from './admin/admin-invoices-page.js';
import { AdminSiteOrderPage, AdminSiteOrdersPage } from './admin/admin-site-orders-page.js';
import { AdminAccessCodesPage, AdminAuditPage, AdminBackupsPage, AdminCouponsPage, AdminEmailSendingPage } from './admin/admin-platform-pages.js';
import { t } from '../../i18n/index.js';

/** @param {{ onSignedIn: (email: string) => void }} props */
function AdminSignIn({ onSignedIn }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState(/** @type {'email' | 'code'} */ ('email'));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendCode = async (/** @type {Event} */ event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await requestAdminEmailCode(email.trim());
      if (response?.devCode) setCode(response.devCode);
      setStep('code');
    } catch (requestError) {
      setError(operationsErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  };

  /** @param {string} value */
  const verify = async (value) => {
    if (value.length !== EMAIL_CODE_LENGTH) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await verifyAdminEmailCode(email.trim(), value);
      onSignedIn(response.email);
    } catch (requestError) {
      setError(operationsErrorMessage(requestError));
      setCode('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return html`<${AuthLayout} title=${step === 'email' ? t('operations.admin.title') : t('operations.admin.codeTitle')} subtitle=${t('operations.admin.subtitle')}>
    ${step === 'email'
      ? html`<form class="auth-form" onSubmit=${sendCode}>
          <${AuthTextField} label=${t('operations.admin.emailLabel')} name="email" type="email" value=${email} onInput=${setEmail} autoComplete="email" autoFocus=${true} error=${error} />
          <${MainButton} title=${t('operations.admin.sendCode')} type="submit" isLoading=${isSubmitting} />
        </form>`
      : html`<form class="auth-form" onSubmit=${(/** @type {Event} */ event) => {
          event.preventDefault();
          verify(code);
        }}>
          <${EmailCodeField} value=${code} onInput=${setCode} onComplete=${verify} error=${error} disabled=${isSubmitting} />
          <${MainButton} title=${t('operations.admin.verify')} type="submit" isLoading=${isSubmitting} disabled=${code.length !== EMAIL_CODE_LENGTH} />
        </form>`}
  <//>`;
}

/**
 * @param {{ section: import('../../lib/admin-navigation.js').AdminSectionKey, id: string | null }} route
 */
function renderAdminSection({ section, id }) {
  switch (section) {
    case 'institutions':
      return id ? html`<${AdminInstitutionPage} key=${id} tenantId=${id} />` : html`<${AdminInstitutionsPage} />`;
    case 'payments':
      return html`<${AdminPaymentsPage} />`;
    case 'invoices':
      return html`<${AdminInvoicesPage} />`;
    case 'site-orders':
      return id ? html`<${AdminSiteOrderPage} key=${id} orderId=${id} />` : html`<${AdminSiteOrdersPage} />`;
    case 'coupons':
      return html`<${AdminCouponsPage} />`;
    case 'access-codes':
      return html`<${AdminAccessCodesPage} />`;
    case 'email-sending':
      return html`<${AdminEmailSendingPage} />`;
    case 'backups':
      return html`<${AdminBackupsPage} />`;
    case 'audit':
      return html`<${AdminAuditPage} />`;
    default:
      return html`<${AdminOverviewPage} />`;
  }
}

/** @param {{ section?: string, id?: string }} props */
export function AdminPage({ section, id }) {
  const [email, setEmail] = useState(/** @type {string | null | undefined} */ (undefined));
  const route = resolveAdminRoute({ section, id });
  useEffect(() => {
    fetchAdminSession()
      .then((session) => setEmail(session?.email ?? null))
      .catch(() => setEmail(null));
  }, []);
  useEffect(() => {
    document.title = `${t(`operations.admin.panel.sections.${route.section}`)} · ${t('operations.admin.documentTitle')}`;
  }, [route.section]);
  if (email === undefined) return html`<div class="admin-page"><${Skeleton} height=${48} /></div>`;
  if (!email) return html`<${AdminSignIn} onSignedIn=${setEmail} />`;
  return html`<${AdminShell} email=${email} section=${route.section} onSignOut=${async () => {
    await signOutAdmin().catch(() => null);
    setEmail(null);
  }}>
    ${renderAdminSection(route)}
  </${AdminShell}>`;
}
